import { useState, useEffect, useMemo, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Slider,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import FleetWebApi from "../Api/FleetWebApi";
import IdList from "./IdList";
import { formatDateTime, formatShort } from "./harpieUtils";

// The two halves of a debug waveform's payload are the device's two channels.
const CH0_COLOR = "#f44336";
const CH1_COLOR = "#2196f3";
const NO_WAVEFORMS = [];

// tofdeltaps waveforms by coreID, one at a time on a slider through the
// coreID's waveforms oldest to newest.
// waveforms: the query's waveform summaries (no payload), newest first.
function DebugWaveformsTab({ waveforms, loaded }) {
  // coreID -> its waveforms oldest first, coreIDs ordered by their newest waveform.
  const byCore = useMemo(() => {
    const groups = new Map();
    for (const waveform of waveforms) {
      if (!groups.has(waveform.coreId)) groups.set(waveform.coreId, []);
      groups.get(waveform.coreId).unshift(waveform);
    }
    return groups;
  }, [waveforms]);

  const [selectedCore, setSelectedCore] = useState(null);
  const core = byCore.has(selectedCore) ? selectedCore : byCore.keys().next().value ?? null;
  const coreWaveforms = byCore.get(core) || NO_WAVEFORMS;

  // The position picked in a coreID's list, which starts on its newest, and
  // the slider's position while dragged.
  const [picked, setPicked] = useState({ list: null, index: 0 });
  const [dragging, setDragging] = useState(null);
  const index = picked.list === coreWaveforms ? picked.index : Math.max(coreWaveforms.length - 1, 0);

  const summary = coreWaveforms[index];
  const position = dragging ?? index;

  // Payloads are fetched one at a time, as shown, and kept for the page's life.
  const cache = useRef(new Map());
  const [waveform, setWaveform] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    setError(null);
    if (!summary) {
      setWaveform(null);
      return;
    }
    if (cache.current.has(summary.id)) {
      setWaveform(cache.current.get(summary.id));
      return;
    }
    let cancelled = false;
    setWaveform(null);
    FleetWebApi.getHarpieWaveform(summary.id)
      .then((result) => {
        cache.current.set(summary.id, result);
        if (!cancelled) setWaveform(result);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [summary]);

  const go = (i) =>
    setPicked({
      list: coreWaveforms,
      index: Math.min(Math.max(Math.round(i), 0), Math.max(coreWaveforms.length - 1, 0)),
    });
  const count = coreWaveforms.length;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4} lg={3}>
        <IdList
          title="Core IDs"
          loaded={loaded}
          items={[...byCore].map(([id, group]) => {
            const latest = group[group.length - 1];
            const proxy = latest.headers.proxy;
            const text = `${group.length} waveforms · latest ${formatShort(latest.timestamp)}`;
            return { id, subtitle: proxy ? `${text} · via ${proxy}` : text };
          })}
          selected={core}
          onSelect={setSelectedCore}
          emptyText="No waveforms with tofdeltaps in this window."
        />
      </Grid>
      <Grid item xs={12} md={8} lg={9}>
        {/* height: auto overrides main.css's .MuiBox-root { height: 100% }, which
            stretches a Box in a grid column to the column's height. */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, height: "auto" }}>
          <Typography variant="h6" sx={{ fontFamily: "monospace", flexGrow: 1 }}>
            {core || "No debug waveforms"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {count ? `${position + 1} of ${count}` : ""}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ChevronLeftIcon />}
            disabled={index <= 0}
            onClick={() => go(index - 1)}
          >
            Older
          </Button>
          <Button
            variant="outlined"
            size="small"
            endIcon={<ChevronRightIcon />}
            disabled={index >= count - 1}
            onClick={() => go(index + 1)}
          >
            Newer
          </Button>
        </Box>
        {summary ? (
          <Paper variant="outlined" sx={{ p: 1, mt: 1 }}>
            <WaveformHeading summary={summary} />
            {error ? (
              <Alert severity="error">{error}</Alert>
            ) : waveform && waveform.id === summary.id ? (
              <WaveformChart waveform={waveform} />
            ) : (
              <Box sx={{ height: 450, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            )}
          </Paper>
        ) : null}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, px: 2, mt: 1, height: "auto" }}>
          <Typography variant="caption" color="text.secondary">
            {count ? formatShort(coreWaveforms[0].timestamp) : ""}
          </Typography>
          <Slider
            value={position}
            min={0}
            max={Math.max(count - 1, 1)}
            step={1}
            marks={count > 1 && count <= 100}
            disabled={count < 2}
            valueLabelDisplay="auto"
            valueLabelFormat={(i) => (coreWaveforms[i] ? formatDateTime(coreWaveforms[i].timestamp) : "")}
            onChange={(e, value) => setDragging(value)}
            onChangeCommitted={(e, value) => {
              setDragging(null);
              go(value);
            }}
            sx={{ flexGrow: 1 }}
          />
          <Typography variant="caption" color="text.secondary">
            {count ? formatShort(coreWaveforms[count - 1].timestamp) : ""}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
}

function WaveformHeading({ summary }) {
  const delta = summary.tofdeltaps === null ? "—" : `${summary.tofdeltaps} ps`;
  const extras = ["tofch0ps", "tofch1ps", "usscfg"]
    .filter((key) => key in summary.headers)
    .map((key) => `${key} ${summary.headers[key]}`)
    .join("   ");
  return (
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 2 }}>
      <Typography variant="subtitle2">{formatDateTime(summary.timestamp)} UTC</Typography>
      <Typography variant="body2">tofdeltaps {delta}</Typography>
      <Typography variant="caption" color="text.secondary">
        {extras}
      </Typography>
    </Box>
  );
}

function WaveformChart({ waveform }) {
  // A null sample (not a number in the payload) leaves a gap in its line.
  const data = useMemo(
    () =>
      Array.from({ length: Math.max(waveform.ch0.length, waveform.ch1.length) }, (_, i) => ({
        i,
        ch0: waveform.ch0[i],
        ch1: waveform.ch1[i],
      })),
    [waveform]
  );
  return (
    <ResponsiveContainer width="100%" height={450}>
      <LineChart data={data} margin={{ top: 10, right: 30, left: 20, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="i"
          type="number"
          domain={[0, Math.max(data.length - 1, 1)]}
          label={{ value: "Sample", position: "insideBottom", offset: -10 }}
        />
        <YAxis
          domain={["auto", "auto"]}
          label={{ value: "Amplitude", angle: -90, position: "insideLeft" }}
        />
        <Tooltip labelFormatter={(i) => `Sample ${i}`} />
        <Legend verticalAlign="top" />
        <Line
          type="linear"
          dataKey="ch0"
          name="Ch0"
          stroke={CH0_COLOR}
          strokeWidth={3}
          dot={false}
          isAnimationActive={false}
        />
        {/* Narrower and on top: the channels often coincide, and Ch0 stays visible as a rim around it. */}
        <Line
          type="linear"
          dataKey="ch1"
          name="Ch1"
          stroke={CH1_COLOR}
          strokeWidth={1.5}
          dot={false}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

export default DebugWaveformsTab;
